import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronRight, Plus, UtensilsCrossed } from 'lucide-react';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription } from './ui/drawer';
import OptimizedImage from './OptimizedImage';
import { useCart } from '../context/CartContext';
import { useVegMode } from '../context/VegModeContext';
import { useTiffinOfTheDay } from '../hooks/useTiffinOfTheDay';
import { visibleTiffinPicks } from '../utils/tiffinPlan';
import { groupsFromStatus, storeOpenFrom } from '../utils/productGroupHours';
import { nowInIst } from '../utils/storeHours';
import { toast } from '../utils/toastPolicy';
import { trackDiscovery } from '../utils/analytics';

const isNonVeg = (tags) => (tags || []).includes('Non Veg');

/** The square veg / non-veg mark the Menu uses. */
function FoodMark({ tags, className = '' }) {
  const nonVeg = isNonVeg(tags);
  return (
    <span
      className={`inline-flex w-3 h-3 border-[1.5px] rounded-sm items-center justify-center shrink-0 ${nonVeg ? 'border-red-600' : 'border-green-600'} ${className}`}
      aria-label={nonVeg ? 'Non veg' : 'Veg'}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${nonVeg ? 'bg-red-600' : 'bg-green-600'}`} />
    </span>
  );
}

/**
 * Tiffin of the Day on the home page (Business Rule #58): a white card (1px
 * border — option D, owner pick 2026-10-21, replacing the orange gradient)
 * directly under the hero banner, 16px gaps above and below — "Explore Tiffin
 * of the Day" in near-black, the price in a peach pill (the plan's MRP crossed
 * out, then the real price, e.g. ~~₹199~~ ₹149, in burnt orange) and the plan's line underneath ("Freshly made curry of the day
 * + Sautéed Veggies + Rice"). Nothing about the paired add-on on the home page
 * (owner decision 2026-10-11) — that is offered on the tiffin's page ("Make it a
 * meal") and in the Cart nudge. Tapping opens a bottom sheet listing today's
 * tiffins (image, veg mark, description, MRP + price) each with **Add**; a
 * tiffin with customizations opens its product page instead.
 *
 * Renders nothing — and takes no space — at a store without the plan, on a day
 * with nothing planned, while the store is closed (admins still see it), when the
 * partner kitchen is outside its hours (Business Rule #52) or before the store's
 * menu list has loaded; veg mode drops non-veg tiffins.
 *
 * @param {{branch: object, storeState: object|null, menuItems: Array, isAdmin?: boolean}} props
 */
export default function TiffinOfTheDayBar({ branch, storeState, menuItems, isAdmin = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { addToCart } = useCart();
  const { vegMode } = useVegMode();
  const { today } = useTiffinOfTheDay(branch?.id || null);
  const [open, setOpen] = useState(false);
  const [tick, setTick] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setTick(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);

  const now = useMemo(() => nowInIst(), [tick]); // eslint-disable-line react-hooks/exhaustive-deps
  const groups = useMemo(() => groupsFromStatus(storeState, branch), [storeState, branch]);
  const storeOpen = storeOpenFrom(storeState, branch, now);
  const picks = useMemo(
    () => visibleTiffinPicks(today.picks, menuItems, { groups, now, storeOpen, vegMode }),
    [today.picks, menuItems, groups, now, storeOpen, vegMode],
  );

  if (!today.active || !picks.length) return null;
  if (!storeOpen && !isAdmin) return null;

  // The bar shows the cheapest of today's tiffins (with its MRP, if set);
  // "from" only when today's tiffins aren't all the same price.
  const cheapest = picks.reduce((a, p) => (p.price < a.price ? p : a), picks[0]);
  const mixedPrices = picks.some(p => p.price !== cheapest.price);
  const tagline = today.tagline ?? '';

  const menuItemFor = (entry) => ({
    id: entry.menu_item_id, name: entry.name, price: entry.price ?? entry.base_price,
    branch_id: branch?.id, image_url: entry.image_url, image_urls: entry.image_urls,
  });

  const openProduct = (pick) => {
    setOpen(false);
    navigate(`/menu/item/${pick.menu_item_id}`, { state: { backgroundLocation: location } });
  };

  const addTiffin = (pick) => {
    if (pick.has_customizations) { openProduct(pick); return; }
    trackDiscovery('home_tiffin_of_the_day', pick.name, branch?.id);
    addToCart(menuItemFor(pick), 1, null, 0);
    toast.cartAdd(`${pick.name} added to cart`);
    setOpen(false);
  };

  return (
    // No top padding: the hero banner's own mb-4 is the gap above; pb-4 matches
    // it below (bar moved under the hero, owner ask 2026-10-21).
    <section className="px-4 pb-4" data-testid="tiffin-of-the-day">
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full flex items-center gap-3 rounded-2xl border bg-card px-4 py-3 text-left text-foreground shadow-sm active:scale-[0.99] transition-transform"
        data-testid="tiffin-bar"
      >
        <div className="flex-1 min-w-0">
          <p className="font-fraunces font-bold text-[16.5px] leading-tight tracking-tight whitespace-nowrap">Explore Tiffin of the Day</p>
          {tagline && (
            <p className="mt-0.5 text-[11.5px] leading-snug text-muted-foreground line-clamp-2" style={{ textWrap: 'balance' }} data-testid="tiffin-bar-tagline">
              {tagline}
            </p>
          )}
        </div>
        {/* The price in a peach pill — the one brand-orange moment on the white card
            (option D, owner pick 2026-10-21). Burnt orange, not the accent itself, so
            the figure passes contrast on the tint. */}
        <div className="shrink-0 rounded-full bg-[hsl(28_92%_94%)] px-3 py-1.5 text-[16px] font-extrabold leading-tight whitespace-nowrap text-[hsl(25_90%_36%)]" data-testid="tiffin-bar-price">
          {cheapest.mrp && <span className="text-[11.5px] font-medium line-through text-muted-foreground mr-1">₹{cheapest.mrp}</span>}
          {mixedPrices && <span className="text-[11px] font-medium text-coffee mr-1">from</span>}
          ₹{cheapest.price}
        </div>
        <ChevronRight className="w-4 h-4 text-foreground shrink-0" strokeWidth={1.8} />
      </button>

      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerContent data-testid="tiffin-sheet">
          <DrawerHeader className="text-left">
            <DrawerTitle className="font-fraunces text-xl">Tiffin of the Day</DrawerTitle>
            <DrawerDescription>Fresh, homestyle and only on today's menu.</DrawerDescription>
          </DrawerHeader>
          <div className="px-4 pb-6 space-y-3 max-h-[65vh] overflow-y-auto">
            {picks.map((pick) => (
              <div key={pick.parent_sku} className="rounded-2xl border bg-card p-3" data-testid={`tiffin-pick-${pick.menu_item_id}`}>
                <div className="flex gap-3">
                  <button type="button" onClick={() => openProduct(pick)} className="w-20 h-20 rounded-xl overflow-hidden bg-muted shrink-0">
                    {(pick.image_urls || pick.image_url) ? (
                      <OptimizedImage src={pick.image_url} imageUrls={pick.image_urls} size="thumb" alt={pick.name} className="w-full h-full" width={80} height={80} />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><UtensilsCrossed className="w-6 h-6 text-muted-foreground" strokeWidth={1.5} /></div>
                    )}
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className="flex items-center gap-1.5 font-semibold leading-tight">
                      <FoodMark tags={pick.tags} />
                      <span className="truncate">{pick.name}</span>
                    </p>
                    {pick.description && (
                      <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{pick.description}</p>
                    )}
                    <p className="mt-1 text-sm">
                      {pick.mrp && <span className="line-through text-muted-foreground mr-1.5">₹{pick.mrp}</span>}
                      <span className="font-semibold">₹{pick.price}</span>
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => addTiffin(pick)}
                  className="mt-3 w-full flex items-center justify-center gap-1.5 rounded-full bg-primary text-primary-foreground px-4 py-2.5 text-sm font-semibold active:scale-[0.98]"
                  data-testid={`tiffin-add-${pick.menu_item_id}`}
                >
                  <Plus className="w-4 h-4" strokeWidth={2} /> Add
                </button>
              </div>
            ))}
          </div>
        </DrawerContent>
      </Drawer>
    </section>
  );
}
